/**
 * 服务端发布操作的三个前端接口(docs/CONTRACT-publish-ops.md §5)。
 *
 * 开关关着的后端(或还没部署这批接口的环境)上,查询会失败或返回空 —— 调用方一律吞掉,
 * 界面维持原样。
 */
import { apiClient } from '../../core/api';

const base = (userId: string) => `/digital-staff/publish-operations/${encodeURIComponent(userId)}`;

/** `apiClient` 把非 401 的 HTTP 错误转成 200 + {errorCode, errorMessage};当失败处理。 */
function throwIfEnvelopeError(body: unknown, fallback: string): void {
  if (body && typeof body === 'object' && 'errorCode' in body && 'errorMessage' in body) {
    throw new Error((body as { errorMessage?: string }).errorMessage || fallback);
  }
}

/** §5.1 —— 本会话所有非 deleted 的 op(原样返回,由 publishOps.mergePublishOps 校验)。 */
export async function listPublishOperations(
  userId: string,
  conversationId: string
): Promise<unknown[]> {
  const resp = await apiClient.chatbotGet<{ operations?: unknown[] }>(base(userId), {
    conversationId,
  });
  const body = resp.data as unknown;
  throwIfEnvelopeError(body, 'Could not load publish progress');
  const ops = (body as { operations?: unknown } | null)?.operations;
  return Array.isArray(ops) ? ops : [];
}

export interface PublishOpActionResult {
  ok: boolean;
  error?: string;
  /** 服务端眼里这条 op 的当前样子 —— 成功失败都可能带,带了就合并进来。 */
  operation?: unknown;
}

async function postAction(
  path: string,
  data: unknown,
  fallback: string
): Promise<PublishOpActionResult> {
  const resp = await apiClient.chatbotPost<PublishOpActionResult>(path, data);
  const body = resp.data as unknown;
  throwIfEnvelopeError(body, fallback);
  const r = (body ?? {}) as PublishOpActionResult;
  return { ok: r.ok === true, error: r.error, operation: r.operation };
}

/** §5.2 —— 放弃一个失败的渠道。 */
export function abandonPublishOperation(
  userId: string,
  operationId: string
): Promise<PublishOpActionResult> {
  return postAction(
    `${base(userId)}/${encodeURIComponent(operationId)}/abandon`,
    undefined,
    'Could not update this channel'
  );
}

/** §5.3 —— 商家确认一个「不知道发没发出去」的渠道到底上没上线。 */
export function confirmPublishOperation(
  userId: string,
  operationId: string,
  live: boolean
): Promise<PublishOpActionResult> {
  return postAction(
    `${base(userId)}/${encodeURIComponent(operationId)}/confirm`,
    { live },
    'Could not update this channel'
  );
}
